import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ArrowRight, Loader2, X, QrCode, Settings, ChevronDown, Home, PawPrint } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import '../styles/landing.css';
import { motion, useScroll, useTransform } from 'framer-motion';
import ProcessingPopup from '../components/ProcessingPopup';
import PromptEditor from '../components/PromptEditor';
import { useFrame } from '../contexts/FrameContext';
import PlacesAutocomplete from '../components/PlacesAutocomplete';
import ImageDropzone from '../components/ImageDropzone';
import { Camera } from 'lucide-react';
import {
  handleCreateSheet,
  handleAssessSuitability,
  generateAIImages,
  startImageProcessingWorkflow,
  savePropertySearch,
  updatePropertySearchAnalysis,
  updatePropertySearchImages,
  updatePropertySearchImageSource,
  parseAddressComponents,
  savePropertySearchForImage,
  analyzeImageAndNotifyWidget
} from '../services/imageProcessing';
import WebcamCapture, { WebcamCaptureRef } from '../components/WebcamCapture';
import { supabase } from '../lib/supabase';
import { v4 as uuidv4 } from 'uuid';
import { PropertySearch } from '../types';
import { notifyImageGeneration, notifyLandingPageVisit } from '../utils/slackNotifications';
import { resetMetaTagsToDefault } from '../utils/metaTags';
import { updateWidgetForLanding } from '../utils/widgetSync';
import {
  initializeGA,
  trackLandingPageView,
  trackAddressSearch,
  trackImageProcessingStarted,
  trackConfiguratorView,
  trackOrderButtonClick,
  trackUserEngagement,
  trackError,
  trackFunnelStep,
  identifyUser,
  getUserIdFromUrl,
  storeUserId,
  setUserIdTracking,
  trackEventWithUserId,
  trackFunnelStepWithUserId,
  trackUserMilestone
} from '../utils/analytics';

// Define structured address interface
interface StructuredAddress {
  formatted_address: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  postal_code?: string;
  country?: string;
  lat?: number;
  lng?: number;
  // Google Places API specific fields
  street_number?: string;
  route?: string;
  locality?: string;
  administrative_area_level_1?: string;
}

// Default property ID for landing page images
const DEFAULT_LANDING_PROPERTY_ID = 'ea52286b-3484-4313-81c8-6403a39023a5';

// Art style image URLs
const WATERCOLOR_IMAGE_URL = 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/propertyimages/watercolor-1749478289092.jpg';
const PENCIL_IMAGE_URL = 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/propertyimages/pencil-1749478331175.jpg';
const OIL_IMAGE_URL = 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/propertyimages/oil-1749478288130.jpg';

// Pet image URLs
const PET_WATERCOLOR_IMAGE_URL = '/Pet1_watercolor.jpg';
const PET_PENCIL_IMAGE_URL = '/Pet1_penci.jpg';
const PET_OIL_IMAGE_URL = '/Pet1_oil.jpg';

// Fallback images if database fetch fails
const FALLBACK_CAROUSEL_IMAGES = [
  WATERCOLOR_IMAGE_URL,
  PENCIL_IMAGE_URL,
  OIL_IMAGE_URL
];

// Pet fallback images
const PET_FALLBACK_CAROUSEL_IMAGES = [
  PET_WATERCOLOR_IMAGE_URL,
  PET_PENCIL_IMAGE_URL,
  PET_OIL_IMAGE_URL
];

// Customer testimonials
const TESTIMONIALS = [
  {
    quote: "I was so impressed with the quality when I received my frame, I ordered another one as a gift for a friend",
    author: "Janos",
    location: "Huntington, NY"
  },
  {
    quote: "This makes for a great closing or anniversary gift",
    author: "Robert",
    location: "Real Estate Agent, East Hampton, NY"
  },
  {
    quote: "I got one of our second home. It's a real centerpiece and conversation starter",
    author: "Margaret",
    location: "Ponte, FL"
  }
];

const CssLanding: React.FC = () => {
  // Add vh calculation for mobile browsers
  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setVH();
    window.addEventListener('resize', setVH);
    return () => window.removeEventListener('resize', setVH);
  }, []);

  // Initialize Google Analytics and track landing page view
  useEffect(() => {
    // Initialize GA4 (add your measurement ID)
    const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
    if (GA_MEASUREMENT_ID) {
      initializeGA(GA_MEASUREMENT_ID);
    }
    
    // Capture user ID from URL for individual tracking
    const userId = getUserIdFromUrl();
    if (userId) {
      console.log('🎯 User ID detected for tracking:', userId);
      storeUserId(userId);
      setUserIdTracking(userId);
      
      // Track user milestone
      trackUserMilestone('landing_page_visit', {
        entry_method: 'direct_link'
      });
    }
    
    // Identify user type
    const userType = hasReturnedFromConfigurator ? 'returning' : 'new';
    identifyUser(userType, imageSource || undefined);
    
    // Track landing page view with user tracking if applicable
    if (userId) {
      trackEventWithUserId('page_view', {
        page_title: 'Landing Page',
        page_category: 'marketing',
        content_group1: 'home',
        user_type: userType,
        has_existing_content: hasReturnedFromConfigurator
      });
      
      trackFunnelStepWithUserId('Landing Page Visit', 1, { 
        user_type: userType,
        has_existing_content: hasReturnedFromConfigurator 
      });
    } else {
      trackLandingPageView();
      trackFunnelStep('Landing Page Visit', 1, { 
        user_type: userType,
        has_existing_content: hasReturnedFromConfigurator 
      });
    }

    // Send Slack notification for landing page visit
    notifyLandingPageVisit();

    // Update feedback widget for landing page
    updateWidgetForLanding();
  }, []); // Run once on mount

  // Reset meta tags to default for landing page
  useEffect(() => {
    // Only reset if we're actually on the landing page (not coming from a token)
    if (window.location.pathname === '/') {
      resetMetaTagsToDefault();
    }
  }, []);

  // Add state for emoji support detection
  const [supportsEmoji, setSupportsEmoji] = useState(false);

  // Detect emoji support on component mount
  useEffect(() => {
    const detectEmojiSupport = () => {
      // Create a canvas to test emoji rendering
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        setSupportsEmoji(false);
        return;
      }
      
      canvas.width = 20;
      canvas.height = 20;
      
      // Set font and draw the flag emoji
      ctx.font = '16px Arial';
      ctx.fillText('🇺🇸', 0, 16);
      
      // Get image data and check if anything was actually rendered
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      // Check if any non-transparent pixels exist
      let hasContent = false;
      for (let i = 3; i < data.length; i += 4) {
        if (data[i] > 0) { // Alpha channel > 0 means visible pixel
          hasContent = true;
          break;
        }
      }
      
      setSupportsEmoji(hasContent);
    };

    detectEmojiSupport();
  }, []);

  // American Flag Component with emoji detection
  const AmericanFlag = () => {
    if (supportsEmoji) {
      return <span className="text-2xl">🇺🇸</span>;
    }
    
    return (
      <span className="w-6 h-6 flex items-center justify-center">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-full"
        >
          {/* Flag background */}
          <rect width="24" height="18" fill="#B22234" />
          
          {/* White stripes */}
          <rect y="1.38" width="24" height="1.38" fill="white" />
          <rect y="4.15" width="24" height="1.38" fill="white" />
          <rect y="6.92" width="24" height="1.38" fill="white" />
          <rect y="9.69" width="24" height="1.38" fill="white" />
          <rect y="12.46" width="24" height="1.38" fill="white" />
          <rect y="15.23" width="24" height="1.38" fill="white" />
          
          {/* Blue canton */}
          <rect width="9.6" height="9.69" fill="#3C3B6E" />
          
          {/* Stars (simplified as dots for better rendering at small size) */}
          <circle cx="1.2" cy="1.2" r="0.3" fill="white" />
          <circle cx="2.4" cy="1.8" r="0.3" fill="white" />
          <circle cx="3.6" cy="1.2" r="0.3" fill="white" />
          <circle cx="4.8" cy="1.8" r="0.3" fill="white" />
          <circle cx="6" cy="1.2" r="0.3" fill="white" />
          <circle cx="7.2" cy="1.8" r="0.3" fill="white" />
          <circle cx="8.4" cy="1.2" r="0.3" fill="white" />
          
          <circle cx="1.8" cy="2.7" r="0.3" fill="white" />
          <circle cx="3" cy="2.1" r="0.3" fill="white" />
          <circle cx="4.2" cy="2.7" r="0.3" fill="white" />
          <circle cx="5.4" cy="2.1" r="0.3" fill="white" />
          <circle cx="6.6" cy="2.7" r="0.3" fill="white" />
          <circle cx="7.8" cy="2.1" r="0.3" fill="white" />
          
          <circle cx="1.2" cy="3.6" r="0.3" fill="white" />
          <circle cx="2.4" cy="4.2" r="0.3" fill="white" />
          <circle cx="3.6" cy="3.6" r="0.3" fill="white" />
          <circle cx="4.8" cy="4.2" r="0.3" fill="white" />
          <circle cx="6" cy="3.6" r="0.3" fill="white" />
          <circle cx="7.2" cy="4.2" r="0.3" fill="white" />
          <circle cx="8.4" cy="3.6" r="0.3" fill="white" />
          
          <circle cx="1.8" cy="5.1" r="0.3" fill="white" />
          <circle cx="3" cy="4.5" r="0.3" fill="white" />
          <circle cx="4.2" cy="5.1" r="0.3" fill="white" />
          <circle cx="5.4" cy="4.5" r="0.3" fill="white" />
          <circle cx="6.6" cy="5.1" r="0.3" fill="white" />
          <circle cx="7.8" cy="4.5" r="0.3" fill="white" />
          
          <circle cx="1.2" cy="6" r="0.3" fill="white" />
          <circle cx="2.4" cy="6.6" r="0.3" fill="white" />
          <circle cx="3.6" cy="6" r="0.3" fill="white" />
          <circle cx="4.8" cy="6.6" r="0.3" fill="white" />
          <circle cx="6" cy="6" r="0.3" fill="white" />
          <circle cx="7.2" cy="6.6" r="0.3" fill="white" />
          <circle cx="8.4" cy="6" r="0.3" fill="white" />
          
          <circle cx="1.8" cy="7.5" r="0.3" fill="white" />
          <circle cx="3" cy="6.9" r="0.3" fill="white" />
          <circle cx="4.2" cy="7.5" r="0.3" fill="white" />
          <circle cx="5.4" cy="6.9" r="0.3" fill="white" />
          <circle cx="6.6" cy="7.5" r="0.3" fill="white" />
          <circle cx="7.8" cy="6.9" r="0.3" fill="white" />
          
          <circle cx="1.2" cy="8.4" r="0.3" fill="white" />
          <circle cx="2.4" cy="9" r="0.3" fill="white" />
          <circle cx="3.6" cy="8.4" r="0.3" fill="white" />
          <circle cx="4.8" cy="9" r="0.3" fill="white" />
          <circle cx="6" cy="8.4" r="0.3" fill="white" />
          <circle cx="7.2" cy="9" r="0.3" fill="white" />
          <circle cx="8.4" cy="8.4" r="0.3" fill="white" />
          
          <circle cx="1.8" cy="9.3" r="0.3" fill="white" />
          <circle cx="3" cy="8.7" r="0.3" fill="white" />
          <circle cx="4.2" cy="9.3" r="0.3" fill="white" />
          <circle cx="5.4" cy="8.7" r="0.3" fill="white" />
          <circle cx="6.6" cy="9.3" r="0.3" fill="white" />
          <circle cx="7.8" cy="8.7" r="0.3" fill="white" />
        </svg>
      </span>
    );
  };

  // Helper function to determine message type for styling
  const getMessageType = (errorMessage: string | null) => {
    if (!errorMessage) return '';
    
    // Check if it's a server busy message
    if (errorMessage.toLowerCase().includes('servers are busy') || 
        errorMessage.toLowerCase().includes('service temporarily busy') ||
        errorMessage.toLowerCase().includes('try again in a few minutes')) {
      return 'message--server-busy';
    }
    
    // Default to error styling for other messages
    return 'message--error';
  };

  const location = useLocation();
  const navigate = useNavigate();
  const [localAddress, setLocalAddress] = useState('');
  const [structuredAddressData, setStructuredAddressData] = useState<StructuredAddress | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingFromDatabase, setIsLoadingFromDatabase] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zillowImages, setZillowImages] = useState<string[]>([]);
  const [zillowMainURL, setZillowMainURL] = useState<string | undefined>(undefined);
  const [webImages, setWebImages] = useState<Array<{
    webUrl: string;
    imageUrl: string;
    uploadedUrl?: string;
    file?: Buffer | string;
  }>>([]);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [showProcessing, setShowProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState(0);
  const [suggestedAddress, setSuggestedAddress] = useState('');
  const [showSuggestion, setShowSuggestion] = useState(false);
  const [dismissedSuggestion, setDismissedSuggestion] = useState(false);
  const [receivedImage, setReceivedImage] = useState<string | null>(null);
  const [showQRCode, setShowQRCode] = useState(false);
  const [useGoogleMaps, setUseGoogleMaps] = useState(true);
  const [processingTimeouts, setProcessingTimeouts] = useState<NodeJS.Timeout[]>([]);
  const [isAssessing, setIsAssessing] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectionReason, setSelectionReason] = useState<string | null>(null);
  const [isCreatingSheet, setIsCreatingSheet] = useState(false);
  const [showImages, setShowImages] = useState(false);
  const [skipAssessment, setSkipAssessment] = useState(false);
  const [currentPropertyId, setCurrentPropertyId] = useState<string | null>(null);
  const [generatedImages, setGeneratedImages] = useState<{
    watercolor: string | null;
    oil: string | null;
    pencil: string | null;
  }>({
    watercolor: null,
    oil: null,
    pencil: null
  });
  
  // Add input focus state
  const [isInputFocused, setIsInputFocused] = useState(false);
  
  // Add state for prompt editor
  const [showPromptEditor, setShowPromptEditor] = useState(false);
  
  // Add state for image upload modal
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [showWebcam, setShowWebcam] = useState(false);
  const webcamRef = React.useRef<WebcamCaptureRef>(null);
  const [isGeneratingFromUpload, setIsGeneratingFromUpload] = useState(false);
  
  // Add state for upload message cycling
  const [uploadSubtextIndex, setUploadSubtextIndex] = useState(0);
  const uploadSubtextIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Add state for uploaded image preview
  const [uploadedImagePreview, setUploadedImagePreview] = useState<string | null>(null);
  
  // Add state for email collection step
  const [showEmailCollection, setShowEmailCollection] = useState(false);
  const [generatedWatercolorUrl, setGeneratedWatercolorUrl] = useState<string | null>(null);
  const [emailCollectionEmail, setEmailCollectionEmail] = useState('');
  const [emailCollectionError, setEmailCollectionError] = useState('');
  const [hasProvidedEmailForUpload, setHasProvidedEmailForUpload] = useState(false);
  const [isFromUploadFlow, setIsFromUploadFlow] = useState(false);
  
  // Add state for upload progress percentage
  const [uploadProgressPercentage, setUploadProgressPercentage] = useState(0);
  
  // Add state for testimonial cycling
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const testimonialIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Add state for single image mode toggle
  const [singleImageMode, setSingleImageMode] = useState(false);
  
  // Add state to track image transition
  const [isTransitioningToGenerated, setIsTransitioningToGenerated] = useState(false);
  
  // Step 3 messages from ProcessingPopup
  const AI_GENERATION_SUBTEXTS = [
    'Creating AI illustration template',
    'Gathering my brushes',
    'Mixing my oil paints',
    'Sharpening my pencils',
    'Creating watercolor illustration',
    'Creating oil painting',
    'Creating pencil drawing'
  ];
  
  const AI_GENERATION_SUBTEXTS_SINGLE = [
    'Creating AI illustration template',
    'Gathering my brushes',
    'Creating watercolor illustration',
    'Finalizing your artwork'
  ];
  
  // Get current subtexts based on mode
  const getCurrentAISubtexts = () => {
    return singleImageMode ? AI_GENERATION_SUBTEXTS_SINGLE : AI_GENERATION_SUBTEXTS;
  };
  
  // Add state for USA availability popup
  const [showUSAPopup, setShowUSAPopup] = useState(false);
  
  // Add state for admin mode
  const [isLiveMode, setIsLiveMode] = useState(true); // Default to live mode
  
  // Add state for home/pet toggle - persist in localStorage
  const [selectedMode, setSelectedMode] = useState<'home' | 'pet'>(() => {
    const saved = localStorage.getItem('selectedMode');
    return (saved === 'pet' || saved === 'home') ? saved : 'home';
  });
  
  // New state to track if user has selected "Input an address" in home mode
  const [showAddressInput, setShowAddressInput] = useState(false);
  
  const {
    address,
    propertyImage,
    selectedFrame,
    selectedStyle,
    selectedSize,
    isOrientaAddress,
    isChapelHouse,
    styleImages,
    propertySearchId,
    addressLine1,
    city,
    state,
    zipCode,
    carouselImages,
    setIsOrientaAddress,
    setIsChapelHouse,
    setCarouselImages,
    setPropertyImage,
    setStyleImages,
    setPropertySearchId,
    setSelectedStyle,
    setImageSource,
    setAddress: setContextAddress,
    imageSource
  } = useFrame();

  // Save selectedMode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('selectedMode', selectedMode);
    // Reset address input state when switching modes
    setShowAddressInput(false);
  }, [selectedMode]);

  // Helper function to check if all 3 main styles are available
  const hasAllThreeStyles = () => {
    return !!(propertyImage && styleImages?.pencil && styleImages?.oil);
  };

  // Helper function to check if at least one image is available (indicating generation started)
  const hasAtLeastOneGeneratedImage = () => {
    return !!(propertyImage || styleImages?.pencil || styleImages?.oil || styleImages?.charcoal || styleImages?.chalk);
  };

  // Helper function to check if there's an active property search (entry created)
  const hasActivePropertySearch = () => {
    return !!(propertySearchId || currentPropertyId);
  };

  // Helper function to check if images are currently being generated
  const isGeneratingImages = () => {
    return !!(isGeneratingAI || isGeneratingFromUpload || showProcessing);
  };

  // Check if user is returning from configurator or has an active session with generated content
  // BUT NOT when currently processing (showProcessing is true)
  const hasReturnedFromConfigurator = hasAllThreeStyles() || 
    (hasActivePropertySearch() && hasAtLeastOneGeneratedImage() && !showProcessing);

  // Check for live mode on component mount and when sessionStorage changes
  useEffect(() => {
    const checkLiveMode = () => {
      const savedMode = sessionStorage.getItem('admin_mode_override');
      setIsLiveMode(savedMode !== 'test'); // Live mode unless explicitly set to test
    };

    checkLiveMode();
    
    // Listen for storage changes (when admin mode is changed in another tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'admin_mode_override') {
        checkLiveMode();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Handle "Try New Address" button click
  const handleTryNewAddress = () => {
    // Clear all generated images and context data
    setPropertyImage(null);
    setStyleImages({
      pencil: null,
      oil: null,
      charcoal: null,
      chalk: null
    });
    setContextAddress('');
    setIsOrientaAddress(false);
    setIsChapelHouse(false);
    setPropertySearchId(null);
    
    // Reset any error states
    setError(null);
    
    // Show address input for returning users
    setShowAddressInput(true);
  };

  // Handle "Input an address" button click for new users
  const handleInputAddressClick = () => {
    setShowAddressInput(true);
  };

  // Handle "Upload a picture" button click for new users
  const handleUploadPictureClick = () => {
    handleUploadLinkClick();
  };

  // Handle "Configure and Buy" button click
  const handleConfigureAndBuy = () => {
    // Check if this is from upload flow and email hasn't been provided
    if (isFromUploadFlow && !hasProvidedEmailForUpload) {
      // Re-show the email collection modal instead of navigating
      setShowImageUploadModal(true);
      setShowEmailCollection(true);
      return;
    }
    
    // Track configurator navigation
    trackFunnelStepWithUserId('Configurator Reached', 4, { 
      image_source: 'existing', 
      has_generated_images: hasAllThreeStyles() 
    });
    trackEventWithUserId('user_engagement', {
      engagement_action: 'configure_button_click',
      returning_user: true,
      content_group1: 'engagement'
    });
    
    navigate('/configure');
  };

  // Get appropriate button text based on generation state
  const getConfigureButtonText = () => {
    return "Configure Frame";
  };

  // Calculate total images count for the processing popup
  const allImagesCount = (receivedImage ? 1 : 0) + zillowImages.length + webImages.length;

  // Get current images to display - prioritize generated images over defaults
  const getCurrentImages = () => {
    const availableImages = [];
    
    // Only use generated images if all 3 styles are available
    if (hasAllThreeStyles()) {
      // All styles available - use generated images
      if (propertyImage) {
        availableImages.push(propertyImage); // Watercolor
      } else {
        availableImages.push(selectedMode === 'pet' ? PET_WATERCOLOR_IMAGE_URL : WATERCOLOR_IMAGE_URL);
      }
      
      if (styleImages?.pencil) {
        availableImages.push(styleImages.pencil);
      } else {
        availableImages.push(selectedMode === 'pet' ? PET_PENCIL_IMAGE_URL : PENCIL_IMAGE_URL);
      }
      
      if (styleImages?.oil) {
        availableImages.push(styleImages.oil);
      } else {
        availableImages.push(selectedMode === 'pet' ? PET_OIL_IMAGE_URL : OIL_IMAGE_URL);
      }
    } else {
      // Not all styles available - use default images based on mode
      if (selectedMode === 'pet') {
        availableImages.push(PET_WATERCOLOR_IMAGE_URL);
        availableImages.push(PET_PENCIL_IMAGE_URL);
        availableImages.push(PET_OIL_IMAGE_URL);
      } else {
        availableImages.push(WATERCOLOR_IMAGE_URL);
        availableImages.push(PENCIL_IMAGE_URL);
        availableImages.push(OIL_IMAGE_URL);
      }
    }
    
    return availableImages;
  };

  const currentDisplayImages = getCurrentImages();

  // Helper function to get the best available image for each style
  const getBestImageForStyle = (style: 'watercolor' | 'pencil' | 'oil') => {
    // Only use generated images if all 3 styles are available
    if (!hasAllThreeStyles()) {
      // Use pet images when in pet mode, otherwise use home images
      if (selectedMode === 'pet') {
        switch (style) {
          case 'watercolor':
            return PET_WATERCOLOR_IMAGE_URL;
          case 'pencil':
            return PET_PENCIL_IMAGE_URL;
          case 'oil':
            return PET_OIL_IMAGE_URL;
          default:
            return PET_WATERCOLOR_IMAGE_URL;
        }
      } else {
        switch (style) {
          case 'watercolor':
            return WATERCOLOR_IMAGE_URL;
          case 'pencil':
            return PENCIL_IMAGE_URL;
          case 'oil':
            return OIL_IMAGE_URL;
          default:
            return WATERCOLOR_IMAGE_URL;
        }
      }
    }

    // All styles available - use generated images
    switch (style) {
      case 'watercolor':
        return propertyImage || (selectedMode === 'pet' ? PET_WATERCOLOR_IMAGE_URL : WATERCOLOR_IMAGE_URL);
      case 'pencil':
        return styleImages?.pencil || (selectedMode === 'pet' ? PET_PENCIL_IMAGE_URL : PENCIL_IMAGE_URL);
      case 'oil':
        return styleImages?.oil || (selectedMode === 'pet' ? PET_OIL_IMAGE_URL : OIL_IMAGE_URL);
      default:
        return propertyImage || (selectedMode === 'pet' ? PET_WATERCOLOR_IMAGE_URL : WATERCOLOR_IMAGE_URL);
    }
  };

  // Helper function to get the best background image for container section
  const getBestContainerImage = () => {
    // Only use generated images if all 3 styles are available
    if (!hasAllThreeStyles()) {
      return selectedMode === 'pet' ? PET_PENCIL_IMAGE_URL : PENCIL_IMAGE_URL; // Default fallback
    }

    // All styles available - prioritize pencil, then oil, then watercolor
    if (styleImages?.pencil) return styleImages.pencil;
    if (styleImages?.oil) return styleImages.oil;
    if (propertyImage) return propertyImage;
    return selectedMode === 'pet' ? PET_PENCIL_IMAGE_URL : PENCIL_IMAGE_URL; // Fallback
  };

  // Function to fetch default property images for landing page
  const fetchDefaultLandingImages = async () => {
    try {
      // Don't fetch defaults if we already have all 3 generated images
      if (hasAllThreeStyles()) {
        console.log('Skipping default image fetch - all 3 generated styles available');
        return;
      }

      console.log('Fetching default landing property data from database with ID:', DEFAULT_LANDING_PROPERTY_ID);
      
      // Fetch the property search data using the same method as menu button
      const { data: propertyData, error: fetchError } = await supabase
        .from('property_searches')
        .select('*')
        .eq('id', DEFAULT_LANDING_PROPERTY_ID)
        .maybeSingle<PropertySearch>();
        
      if (fetchError) {
        console.error('Error fetching default property data:', fetchError);
        console.warn('Failed to fetch default property data, using fallback images:', fetchError.message);
        setCarouselImages(selectedMode === 'pet' ? PET_FALLBACK_CAROUSEL_IMAGES : FALLBACK_CAROUSEL_IMAGES);
        return;
      }
      
      if (!propertyData) {
        console.warn('Default property not found in database, using fallback images');
        setCarouselImages(selectedMode === 'pet' ? PET_FALLBACK_CAROUSEL_IMAGES : FALLBACK_CAROUSEL_IMAGES);
        return;
      }
      
      console.log('Default property data fetched:', propertyData);
      
      // Extract the AI-generated illustrations (pencil, oil, watercolor)
      const aiIllustrations: string[] = [];
      
      // Add images in a specific order, filtering out any null/empty values
      if (propertyData.watercolor_image_url?.trim()) {
        aiIllustrations.push(propertyData.watercolor_image_url);
      }
      if (propertyData.pencil_image_url?.trim()) {
        aiIllustrations.push(propertyData.pencil_image_url);
      }
      if (propertyData.oil_image_url?.trim()) {
        aiIllustrations.push(propertyData.oil_image_url);
      }
      
      // Update carousel images if we have AI illustrations
      if (aiIllustrations.length > 0) {
        console.log('Setting carousel images to AI illustrations:', aiIllustrations);
        setCarouselImages(aiIllustrations);
      } else {
        console.log('No AI illustrations found, using fallback images');
        setCarouselImages(selectedMode === 'pet' ? PET_FALLBACK_CAROUSEL_IMAGES : FALLBACK_CAROUSEL_IMAGES);
      }
      
    } catch (error) {
      console.error('Error loading default property from database:', error);
      // Fallback to default images if database fetch fails
      console.log('Using fallback carousel images due to error');
      setCarouselImages(selectedMode === 'pet' ? PET_FALLBACK_CAROUSEL_IMAGES : FALLBACK_CAROUSEL_IMAGES);
    }
  };

  // Function to fetch property data from database and go to configurator (existing menu button logic)
  const handleMenuButtonClick = async () => {
    const PROPERTY_ID = '93a693bf-be61-4541-8f72-d7fb65a787ce';
    
    setIsLoadingFromDatabase(true);
    setError(null);
    
    try {
      console.log('Fetching property data from database with ID:', PROPERTY_ID);
      
      // Fetch the property search data
      const { data: propertyData, error: fetchError } = await supabase
        .from('property_searches')
        .select('*')
        .eq('id', PROPERTY_ID)
        .maybeSingle<PropertySearch>();
        
      if (fetchError) {
        console.error('Error fetching property data:', fetchError);
        throw new Error(`Failed to fetch property data: ${fetchError.message}`);
      }
      
      if (!propertyData) {
        throw new Error('Property not found in database');
      }
      
      console.log('Property data fetched:', propertyData);
      
      // Set the context values based on the database data
      setPropertySearchId(propertyData.id);
      setContextAddress(propertyData.address);
      
      // Set the property image (watercolor as primary, fallback to master_image_url)
      const primaryImage = propertyData.watercolor_image_url || propertyData.master_image_url;
      if (primaryImage) {
        setPropertyImage(primaryImage);
      }
      
      // Set the style images
      setStyleImages({
        pencil: propertyData.pencil_image_url || null,
        oil: propertyData.oil_image_url || null,
        charcoal: propertyData.charcoal_image_url || null,
        chalk: propertyData.chalk_image_url || null
      });
      
      // Check if this is a special address
      const addressLower = propertyData.address.toLowerCase();
      if (addressLower.includes('2 chapel hill') && 
          addressLower.includes('sherman') && 
          addressLower.includes('ct')) {
        setIsOrientaAddress(false);
        setIsChapelHouse(true);
      } else {
        setIsOrientaAddress(false);
        setIsChapelHouse(false);
      }
      
      console.log('Context values set, navigating to configurator...');
      
      // Navigate directly to the configurator
      navigate('/configure');
      
    } catch (error) {
      console.error('Error loading property from database:', error);
      setError(error instanceof Error ? error.message : 'Failed to load property from database');
    } finally {
      setIsLoadingFromDatabase(false);
    }
  };

  // Load default landing page images on component mount, but only if no generated images exist
  useEffect(() => {
    // Check if we already have all 3 generated images - if so, use them immediately
    if (hasAllThreeStyles()) {
      // Immediately set the carousel to generated images
      const updatedImages = getCurrentImages();
      setCarouselImages(updatedImages);
    } else {
      // Don't have all 3 styles - fetch and use default images
      fetchDefaultLandingImages();
    }
  }, []); // Empty dependency array - only run on mount

  // Update carousel images when toggle mode changes
  useEffect(() => {
    // Only update if we don't have generated images
    if (!hasAllThreeStyles()) {
      // Update to appropriate default images based on mode
      const newImages = selectedMode === 'pet' ? PET_FALLBACK_CAROUSEL_IMAGES : FALLBACK_CAROUSEL_IMAGES;
      setCarouselImages(newImages);
    }
  }, [selectedMode]); // Run when toggle changes

  // Update carousel images when generated images become available (after mount)
  useEffect(() => {
    if (hasAllThreeStyles()) {
      const updatedImages = getCurrentImages();
      setCarouselImages(updatedImages);
    }
  }, [propertyImage, styleImages]);

  // Real-time detection: Switch from defaults to generated images when all 3 become available
  useEffect(() => {
    // Check if we just completed all 3 styles (transition from partial to complete)
    if (hasAllThreeStyles()) {
      console.log('🎨 All 3 styles now available! Switching from defaults to generated images...');
      
      // Force update all image displays to use generated images
      const updatedImages = getCurrentImages();
      setCarouselImages(updatedImages);
      
      // The UI will automatically update due to hasReturnedFromConfigurator change
      // and all getBestImageForStyle() calls will now return generated images
    }
  }, [propertyImage, styleImages?.pencil, styleImages?.oil]); // Monitor each style individually

  // Check if we're returning from contact sheet page
  useEffect(() => {
    if (location.state?.returnFromContactSheet) {
      // Restore processing popup state
      setShowProcessing(true);
      setProcessingStep(1); // Set to the image assessment step
      setShowImages(true); // Show the images panel
      
      // Restore state from location
      if (location.state.receivedImage) setReceivedImage(location.state.receivedImage);
      if (location.state.zillowImages) setZillowImages(location.state.zillowImages);
      if (location.state.webImages) setWebImages(location.state.webImages);
      if (location.state.selectedImage) setSelectedImage(location.state.selectedImage);
      if (location.state.selectionReason) setSelectionReason(location.state.selectionReason);
      if (location.state.zillowMainURL) setZillowMainURL(location.state.zillowMainURL);
      
      // Clean up location state to prevent repeated processing
      navigate('/', { replace: true });
    }
  }, [location.state, navigate]);

  const clearProcessingTimeouts = () => {
    processingTimeouts.forEach(timeout => clearTimeout(timeout));
    setProcessingTimeouts([]);
  };
  
  // Auto-run image assessment when images are loaded
  useEffect(() => {
    console.log('🔍 [Assessment useEffect] Checking conditions...');
    console.log('  - showProcessing:', showProcessing);
    console.log('  - processingStep:', processingStep);
    console.log('  - selectedImage:', !!selectedImage);
    console.log('  - isAssessing:', isAssessing);
    console.log('  - skipAssessment:', skipAssessment);
    console.log('  - receivedImage:', !!receivedImage);
    console.log('  - zillowImages.length:', zillowImages.length);
    console.log('  - webImages.length:', webImages.length);
    
    // If we have images and have reached step 1, and no selection yet
    if (showProcessing && processingStep === 1 && !selectedImage && !isAssessing && !skipAssessment) {
      // Ensure we have some images to assess
      const hasImages = (receivedImage !== null || zillowImages.length > 0 || webImages.length > 0);
      console.log('✅ [Assessment useEffect] All conditions met, hasImages:', hasImages);
      
      if (hasImages) {
        console.log('🚀 [Assessment useEffect] Triggering handleImageAssessment...');
        // Automatically trigger image assessment
        handleImageAssessment();
      } else {
        console.log('⚠️ [Assessment useEffect] No images available for assessment - opening upload modal');
        // No images found, close processing and open upload modal
        setError("We couldn't find a suitable image for this address. Please upload a photo of your home to continue.");
        setShowProcessing(false);
        setShowImageUploadModal(true);
      }
    } else {
      console.log('❌ [Assessment useEffect] Conditions not met for triggering assessment');
    }
  }, [showProcessing, processingStep, selectedImage, isAssessing, receivedImage, zillowImages, webImages, skipAssessment]);
  
  // Auto-run AI generation when a suitable image is selected
  useEffect(() => {
    // If we have a selected image, have reached step 2, and haven't started generating
    if (showProcessing && processingStep === 2 && selectedImage && !isGeneratingAI && !generatedImages.watercolor) {
      // Check if confidence is low before proceeding
      const hasLowConfidence = selectionReason?.includes('Confidence Level: LOW') || false;
      
      // Special case for Chapel Hill address: always proceed even with low confidence
      const isChapelHillAddress = address.toLowerCase().includes('2 chapel hill') && 
                               address.toLowerCase().includes('sherman') && 
                               address.toLowerCase().includes('ct');
                               
      // Only proceed with AI generation if confidence is NOT low or it's the Chapel Hill address
      if (!hasLowConfidence || isChapelHillAddress) {
        // Automatically trigger AI generation
        handleGenerateAI();
      }
    }
  }, [showProcessing, processingStep, selectedImage, isGeneratingAI, generatedImages, selectionReason, address]);

  const handleCreateContactSheet = async () => {
    // Collect all available images - ensure we use uploadedUrl for web images
    const urls = [
      ...(receivedImage ? [receivedImage] : []),
      ...zillowImages,
      ...webImages.map(w => w.uploadedUrl || w.imageUrl)
    ].filter(url => url && url.trim() !== '');

    // Navigate to the contact sheet page
    await handleCreateSheet(
      urls, 
      setIsCreatingSheet,
      setError,
      () => {}, // We don't need setSheetImageUrl
      () => {}, // We don't need setShowSheetLightbox
      (imageUrl) => {
        // Pass all required state to preserve when returning
        navigate('/contact-sheet', { 
          state: { 
            imageUrl,
            receivedImage,
            zillowImages,
            webImages,
            selectedImage,
            selectionReason,
            zillowMainURL
          }
        });
      }
    );
  };

  const handleImageAssessment = async () => {
    console.log('🎯 [handleImageAssessment] Starting assessment...');
    console.log('  - currentPropertyId:', currentPropertyId);
    
    if (!currentPropertyId) {
      console.warn('⚠️ [handleImageAssessment] No property ID available for assessment. This should not happen normally.');
    }
    
    // Create arrays for URLs and image details
    const imageUrls = [
      ...(receivedImage ? [receivedImage] : []), 
      ...zillowImages, 
      ...webImages.map(img => img.uploadedUrl || img.imageUrl)
    ].filter(Boolean);
    
    console.log('📊 [handleImageAssessment] Image URLs prepared:', imageUrls);
    
    // Create detailed image information
    let imageDetails = [];
    let imageNumber = 1;
    
    // Add Street View image if available
    if (receivedImage) {
      imageDetails.push({ type: 'Street View' as const, url: receivedImage, number: imageNumber++ });
    }
    
    // Add Zillow images
    zillowImages.forEach(url => {
      imageDetails.push({ type: 'Zillow' as const, url, number: imageNumber++ });
    });
    
    // Add web images
    webImages.forEach(img => {
      imageDetails.push({ type: 'Web' as const, url: img.uploadedUrl || img.imageUrl, number: imageNumber++ });
    });
    
    console.log('📋 [handleImageAssessment] Image details prepared:', imageDetails);
    
    // Check if this is the Chapel Hill address
    const isChapelHillAddress = address.toLowerCase().includes('2 chapel hill') && 
                              address.toLowerCase().includes('sherman') && 
                              address.toLowerCase().includes('ct');

    try {
      console.log('🚀 [handleImageAssessment] Calling handleAssessSuitability...');
      // Perform the normal assessment
      const analysisResult = await handleAssessSuitability(
        imageUrls,
        zillowImages.length > 0,
        imageDetails,
        setIsAssessing,
        setError,
        setSelectedImage,
        setSelectionReason,
        receivedImage,
        address,
        zillowMainURL
      );
      
      // Special handling for Chapel Hill address
      if (isChapelHillAddress && selectedImage) {
        // Override the confidence level for Chapel Hill address
        let enhancedReason = selectionReason || 'Selected for processing';
        
        // Replace any low confidence with high confidence
        if (enhancedReason.includes('Confidence Level: LOW') || enhancedReason.includes('Confidence Level: MEDIUM')) {
          enhancedReason = enhancedReason.replace(/Confidence Level: (LOW|MEDIUM)/, 'Confidence Level: HIGH');
        } else if (!enhancedReason.includes('Confidence Level:')) {
          enhancedReason += '\n\nConfidence Level: HIGH';
        }
        
        // Update the selection reason with high confidence
        setSelectionReason(enhancedReason);
      }
      
      // Update the property_searches table with analysis results if we have a property ID
      if (currentPropertyId && analysisResult) {
        try {
          await updatePropertySearchAnalysis(currentPropertyId, {
            contactSheetUrl: analysisResult.contactSheetUrl,
            selectedImageUrl: analysisResult.selectedImageUrl,
            suitabilityScore: analysisResult.suitabilityScore,
            confidenceLevel: analysisResult.confidenceLevel
          });
        } catch (updateError) {
          console.error('Error updating property search with analysis results:', updateError);
          // Continue with the flow even if update fails
        }
      }
      
      // Update processing step after successful assessment
      setProcessingStep(2);
    } catch (error) {
      console.error('Error assessing suitability:', error);
      setError(error instanceof Error ? error.message : 'Failed to assess image suitability');
    }
  };

  const handleGenerateAI = async () => {
    // Check if confidence is low before proceeding
    const hasLowConfidence = selectionReason?.includes('Confidence Level: LOW') || false;
    
    // Special case for Chapel Hill address: always proceed even with low confidence
    const isChapelHillAddress = address.toLowerCase().includes('2 chapel hill') && 
                              address.toLowerCase().includes('sherman') && 
                              address.toLowerCase().includes('ct');
    
    // Don't proceed if confidence is low and it's not Chapel Hill address
    if (hasLowConfidence && !isChapelHillAddress) {
      console.log('Skipping AI generation due to low confidence');
      return;
    }
    
    try {
      // Pass the property search ID and mode to generateAIImages
      const firstImageResult = await generateAIImages(
        selectedImage,
        currentPropertyId,
        setIsGeneratingAI,
        setError,
        setGeneratedImages,
        setPropertyImage,
        setStyleImages,
        singleImageMode,
        selectedMode
      );
      
      console.log('First image ready:', firstImageResult);
      
      // Update processing step after the first image is ready
      setProcessingStep(3);

      // Pass property search ID to context and navigate to configure page immediately
      if (currentPropertyId) {
        setPropertySearchId(currentPropertyId);
      }
      
      // Set image source for address-based generation
      setImageSource('address');
      
      // Update database with image source
      if (currentPropertyId) {
        await updatePropertySearchImageSource(currentPropertyId, 'address');
      }

      // Navigate immediately when first image is ready
      setShowProcessing(false);
      
      // Send Slack notification for image generation with the first generated image
      await notifyImageGeneration('address', address, selectedMode, firstImageResult?.firstImageUrl);
      
      navigate('/configure');
    } catch (error) {
      console.error('Error generating AI images:', error);
      setError(error instanceof Error ? error.message : 'Failed to generate AI images');
    }
  };

  // Email validation helper
  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };
  
  // Handle email collection submission
  const handleEmailCollectionSubmit = async () => {
    if (!emailCollectionEmail.trim()) {
      setEmailCollectionError('Please enter your email');
      return;
    }
    
    if (!isValidEmail(emailCollectionEmail.trim())) {
      setEmailCollectionError('Please enter a valid email address');
      return;
    }
    
    const trimmedEmail = emailCollectionEmail.trim();
    
    // Clear error
    setEmailCollectionError('');
    
    // Mark that email has been provided for upload flow
    setHasProvidedEmailForUpload(true);
    
    console.log('📧 Storing email:', trimmedEmail);
    console.log('🔍 Current propertySearchId:', propertySearchId);
    
    // Store email in localStorage as fallback
    localStorage.setItem('customer_email', trimmedEmail);
    console.log('✅ Email saved to localStorage');
    
    // Store email in database if we have a propertySearchId
    if (propertySearchId) {
      try {
        console.log('💾 Updating database with email for property search:', propertySearchId);
        const { data, error: updateError } = await supabase
          .from('property_searches')
          .update({ 
            customer_email: trimmedEmail,
            updated_at: new Date().toISOString()
          })
          .eq('id', propertySearchId)
          .select();
        
        if (updateError) {
          console.error('❌ Error updating email in database:', updateError);
        } else {
          console.log('✅ Email stored in database for property search:', propertySearchId);
          console.log('✅ Updated record:', data);
        }
      } catch (error) {
        console.error('❌ Failed to store email in database:', error);
      }
    } else {
      console.log('ℹ️ No propertySearchId available, email stored only in localStorage');
    }
    
    // Send email to widget via dedicated tracking event
    if (typeof window !== 'undefined') {
      try {
        // Update widget config with email
        if ((window as any).updateWidgetConfig) {
          (window as any).updateWidgetConfig({
            email: trimmedEmail
          });
          console.log('✅ Email sent to widget config:', trimmedEmail);
        }
        
        // Track email submission as a conversion event
        if ((window as any).trackConversion) {
          (window as any).trackConversion('email_provided', {
            email: trimmedEmail
          });
          console.log('✅ Email submission tracked as conversion event');
        }
      } catch (error) {
        console.error('❌ Failed to send email to widget:', error);
      }
    }
    
    // Track successful generation and navigation
    trackFunnelStepWithUserId('Configurator Reached', 4, { 
      image_source: imageSource || 'upload',
      email_provided: true
    });
    trackEventWithUserId('user_engagement', {
      engagement_action: 'email_provided_after_generation',
      email: trimmedEmail,
      content_group1: 'engagement'
    });
    
    // Send notification with generated image
    await notifyImageGeneration(imageSource || 'upload', undefined, selectedMode, generatedWatercolorUrl || undefined);
    
    // Close modal and navigate to configurator
    setShowImageUploadModal(false);
    setShowEmailCollection(false);
    setEmailCollectionEmail('');
    navigate('/configure');
  };

  const handleUploadImage = async (file?: File) => {
    if (!file) return;
    
    try {
      setIsGeneratingFromUpload(true);
      setError(null);
      
      // Track image upload started
      trackEventWithUserId('image_processing_started', {
        image_source: 'upload',
        has_address: false,
        content_group1: 'processing'
      });
      trackFunnelStepWithUserId('Image Processing Started', 3, { image_source: 'upload' });
      
      // Create preview URL for the uploaded file
      const previewUrl = URL.createObjectURL(file);
      setUploadedImagePreview(previewUrl);
      
      // Upload file to Supabase storage
      const filename = `uploaded-${uuidv4()}.jpg`;
      const { data, error } = await supabase.storage
        .from('propertyimages')
        .upload(filename, file, {
          cacheControl: '3600',
          contentType: 'image/jpeg'
        });

      if (error) throw error;
      
      // Get the public URL
      const { data: urlData } = supabase.storage
        .from('propertyimages')
        .getPublicUrl(filename);
      
      // Track successful image upload in widget with URL
      window.trackInteraction?.('image_uploaded', {
        mode: selectedMode,
        file_size_kb: Math.round(file.size / 1024),
        file_type: file.type,
        upload_success: true,
        image_url: urlData.publicUrl,
        file_name: filename,
        supabase_bucket: 'propertyimages'
      });
      
      // Analyze image and send to widget (non-blocking)
      analyzeImageAndNotifyWidget(urlData.publicUrl, 'upload');
      
      // CRITICAL FIX: Create proper property search record with UUID session
      console.log('=== CREATING PROPERTY SEARCH FOR IMAGE UPLOAD ===');
      const propertySearchId = await savePropertySearchForImage('upload', urlData.publicUrl);
      
      if (!propertySearchId) {
        throw new Error('Failed to create property search record for uploaded image');
      }
      
      console.log('✅ Created property search record:', propertySearchId);
      setPropertySearchId(propertySearchId);
      
      // Generate AI images with the proper property search ID and mode
      const firstImageResult = await generateAIImages(
        urlData.publicUrl,
        propertySearchId, // Now passing the real property search ID
        setIsGeneratingAI,
        setError,
        setGeneratedImages,
        setPropertyImage,
        setStyleImages,
        singleImageMode,
        selectedMode
      );
      
      // Store the watercolor image URL for the email collection step
      setGeneratedWatercolorUrl(firstImageResult?.firstImageUrl || null);
      
      // Mark this as an upload flow (not address-based)
      setIsFromUploadFlow(true);
      setHasProvidedEmailForUpload(false);
      
      // Show email collection screen instead of immediately navigating
      setIsGeneratingFromUpload(false);
      setShowEmailCollection(true);
      
    } catch (error) {
      console.error('Error uploading image:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload image';
      setError(errorMessage);
      
      // Track error
      trackError('image_upload', errorMessage, 'upload');
    } finally {
      setIsGeneratingFromUpload(false);
      // Clean up preview URL
      if (uploadedImagePreview) {
        URL.revokeObjectURL(uploadedImagePreview);
        setUploadedImagePreview(null);
      }
    }
  };

  // Handle webcam capture
  const handleImageCapture = async (imageData: string) => {
    try {
      setIsGeneratingFromUpload(true);
      setError(null);
      
      // Track webcam capture started
      trackEventWithUserId('image_processing_started', {
        image_source: 'webcam',
        has_address: false,
        content_group1: 'processing'
      });
      trackFunnelStepWithUserId('Image Processing Started', 3, { image_source: 'webcam' });
      
      // Set the preview to the captured image data
      setUploadedImagePreview(imageData);
      
      // Convert base64 to blob
      const base64Response = await fetch(imageData);
      const blob = await base64Response.blob();
      
      // Create a file from the blob
      const filename = `webcam-${uuidv4()}.jpg`;
      const file = new File([blob], filename, { type: 'image/jpeg' });
      
      // Set image source for webcam capture
      setImageSource('webcam');
      
      // Upload file to Supabase storage
      const { data, error } = await supabase.storage
        .from('propertyimages')
        .upload(filename, file, {
          cacheControl: '3600',
          contentType: 'image/jpeg'
        });

      if (error) throw error;
      
      // Get the public URL
      const { data: urlData } = supabase.storage
        .from('propertyimages')
        .getPublicUrl(filename);
      
      // Track successful webcam capture in widget with URL
      window.trackInteraction?.('image_uploaded', {
        mode: selectedMode,
        file_size_kb: Math.round(file.size / 1024),
        file_type: file.type,
        upload_success: true,
        image_url: urlData.publicUrl,
        file_name: filename,
        supabase_bucket: 'propertyimages',
        capture_method: 'webcam'
      });
      
      // Analyze image and send to widget (non-blocking)
      analyzeImageAndNotifyWidget(urlData.publicUrl, 'webcam');
      
      // CRITICAL FIX: Create proper property search record with UUID session
      console.log('=== CREATING PROPERTY SEARCH FOR WEBCAM CAPTURE ===');
      const propertySearchId = await savePropertySearchForImage('webcam', urlData.publicUrl);
      
      if (!propertySearchId) {
        throw new Error('Failed to create property search record for webcam capture');
      }
      
      console.log('✅ Created property search record:', propertySearchId);
      setPropertySearchId(propertySearchId);
      
      // Generate AI images with the proper property search ID and mode
      const firstImageResult = await generateAIImages(
        urlData.publicUrl,
        propertySearchId, // Now passing the real property search ID
        setIsGeneratingAI,
        setError,
        setGeneratedImages,
        setPropertyImage,
        setStyleImages,
        singleImageMode,
        selectedMode
      );
      
      // Store the watercolor image URL for the email collection step
      setGeneratedWatercolorUrl(firstImageResult?.firstImageUrl || null);
      
      // Mark this as an upload flow (not address-based)
      setIsFromUploadFlow(true);
      setHasProvidedEmailForUpload(false);
      
      // Show email collection screen instead of immediately navigating
      setIsGeneratingFromUpload(false);
      setShowEmailCollection(true);
      
    } catch (error) {
      console.error('Error processing webcam image:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to process image';
      setError(errorMessage);
      
      // Track error
      trackError('webcam_capture', errorMessage, 'webcam');
    } finally {
      setIsGeneratingFromUpload(false);
      // Clean up preview URL
      if (uploadedImagePreview) {
        URL.revokeObjectURL(uploadedImagePreview);
        setUploadedImagePreview(null);
      }
    }
  };

  // Enhanced address selection handler that captures structured data
  const handleAddressSelect = async (selectedAddress: string, structuredAddress?: StructuredAddress) => {
    console.log('Address selected:', selectedAddress);
    console.log('Structured address data:', structuredAddress);
    
    // Track autocomplete address selection
    trackEventWithUserId('address_search', {
      address_length: selectedAddress.length,
      search_method: 'autocomplete',
      has_structured_data: !!structuredAddress,
      content_group1: 'search'
    });
    trackEventWithUserId('user_engagement', {
      engagement_action: 'address_autocomplete_select',
      address_length: selectedAddress.length,
      has_structured_data: !!structuredAddress,
      content_group1: 'engagement'
    });
    
    // If we have a postal_code but no zip_code, copy it over
    if (structuredAddress && structuredAddress.postal_code && !structuredAddress.zip_code) {
      structuredAddress = {
        ...structuredAddress,
        zip_code: structuredAddress.postal_code
      };
    }
    
    if (!structuredAddress?.postal_code && !structuredAddress?.zip_code) {
      console.log('Attempting to find better match for:', selectedAddress);
      
      // Get predictions one more time to ensure fresh results
      try {
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/places`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            'x-client-info': 'places-autocomplete'
          },
          body: JSON.stringify({ input: selectedAddress })
        });

        if (response.ok) {
          const data = await response.json();
          if (data?.predictions?.[0]) {
            // Get details for the best matching prediction
            const placeDetails = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/places`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
                'x-client-info': 'places-details'
              },
              body: JSON.stringify({ place_id: data.predictions[0].place_id })
            });

            if (placeDetails.ok) {
              const detailsData = await placeDetails.json();
              if (detailsData?.result) {
                const components = detailsData.result.address_components || [];
                let streetNumber = '';
                let route = '';
                let city = '';
                let state = '';
                let zipCode = '';
                let country = '';
                let subpremise = '';

                // Extract components from Google's response
                components.forEach((component: any) => {
                  const types = component.types;
                  if (types.includes('street_number')) {
                    streetNumber = component.long_name;
                  } else if (types.includes('route')) {
                    route = component.long_name;
                  } else if (types.includes('subpremise')) {
                    subpremise = component.long_name;
                  } else if (types.includes('locality')) {
                    city = component.long_name;
                  } else if (types.includes('administrative_area_level_1')) {
                    state = component.short_name;
                  } else if (types.includes('postal_code')) {
                    zipCode = component.long_name;
                  } else if (types.includes('country')) {
                    country = component.short_name;
                  }
                });

                // Construct address lines
                const addressLine1 = `${streetNumber} ${route}`.trim();
                const addressLine2 = subpremise;

                structuredAddress = {
                  formatted_address: detailsData.result.formatted_address,
                  address_line_1: addressLine1,
                  address_line_2: addressLine2,
                  city: city,
                  state: state,
                  zip_code: zipCode,
                  postal_code: zipCode,
                  country: country || 'US',
                  lat: detailsData.result.geometry?.location?.lat,
                  lng: detailsData.result.geometry?.location?.lng,
                  // Keep original Google Places API fields
                  street_number: streetNumber,
                  route: route,
                  locality: city,
                  administrative_area_level_1: state
                };
              }
            }
          }
        }
      } catch (error) {
        console.error('Error finding better match:', error);
      }
    }
    
    // Debug log the postal code specifically
    if (structuredAddress?.zip_code) {
      console.log('ZIP CODE CAPTURED:', structuredAddress.zip_code);
    } else {
      console.log('No postal code in structured address data');
    }
    
    setLocalAddress(selectedAddress);
    setStructuredAddressData(structuredAddress || null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let trimmedAddress = localAddress.trim();
    setShowProcessing(false);
    setProcessingStep(0);
    setShowImages(false);
    setGeneratedImages({
      watercolor: null,
      oil: null,
      pencil: null
    });
    
    // Reset stored images in context when entering a new address
    setPropertyImage(null);
    setStyleImages({
      pencil: null,
      oil: null,
      charcoal: null,
      chalk: null
    });
    
    // Reset the skip assessment flag when starting a new address search
    setSkipAssessment(false);
    
    // Reset the current property ID
    setCurrentPropertyId(null);
    
    if (!trimmedAddress) {
      setError('Please enter an address');
      return;
    }

    // Track address submission in widget
    window.trackInteraction?.('address_submitted', {
      mode: selectedMode,
      has_full_address: trimmedAddress.length > 10,
      address_length: trimmedAddress.length
    });

    // Track address search
    trackEventWithUserId('address_search', {
      address_length: trimmedAddress.length,
      search_method: 'manual',
      content_group1: 'search'
    });
    trackFunnelStepWithUserId('Address Search', 2, { address_length: trimmedAddress.length });

    setIsSubmitting(true);
    setError(null);

    try {
      // Convert structured address data to address components
      const addressComponents = structuredAddressData ? {
        address_line_1: structuredAddressData.address_line_1 || undefined,
        address_line_2: structuredAddressData.address_line_2 || undefined,
        city: structuredAddressData.city || structuredAddressData.locality || undefined,
        state: structuredAddressData.state || structuredAddressData.administrative_area_level_1 || undefined,
        zip_code: structuredAddressData.zip_code || structuredAddressData.postal_code || undefined,
        country: structuredAddressData.country || 'US'
      } : parseAddressComponents(localAddress);

      // Debug log the address components
      console.log('Saving address components:', addressComponents);
      console.log('ZIP code being saved:', (addressComponents as any).zipCode || (addressComponents as any).zip_code);

      // Use the enhanced address components for saving to database
      const propertyId = await savePropertySearch(localAddress, null, addressComponents);
        setCurrentPropertyId(propertyId);
        setPropertySearchId(propertyId); // Store in context immediately
        console.log('Saved property search with ID:', propertyId);
        console.log('Address components saved:', addressComponents);
      
      // Special case for 2 Chapel Hill Rd, Sherman, CT
      if (trimmedAddress.toLowerCase().includes('2 chapel hill') && 
          trimmedAddress.toLowerCase().includes('sherman') &&
          trimmedAddress.toLowerCase().includes('ct')) {
        setIsOrientaAddress(false);
        setIsChapelHouse(true);
        setContextAddress(trimmedAddress);
        setShowProcessing(true);
        
        // Start with step 0
        setProcessingStep(0);
      }

      // Track image processing started
      trackEventWithUserId('image_processing_started', {
        image_source: 'address',
        has_address: !!trimmedAddress,
        content_group1: 'processing'
      });
      trackFunnelStepWithUserId('Image Processing Started', 3, { image_source: 'address' });

      // For all other addresses, use the processing workflow
      try {
        setShowProcessing(true);
        
        // Start with step 0 - Looking for images
        setProcessingStep(0);
        
        // Process images
        const result = await startImageProcessingWorkflow(
          trimmedAddress,
          setShowProcessing,
          setProcessingStep,
          setIsSubmitting,
          setError,
          setReceivedImage,
          setZillowImages,
          setWebImages,
          addressComponents
        );
        
        // Store the property ID from the result
        if (result && result.propertyId) {
          setCurrentPropertyId(result.propertyId);
          setPropertySearchId(result.propertyId); // Store in context
        }
        
        // Store the Zillow main URL if provided
        if (result && result.zillowMainURL) {
          setZillowMainURL(result.zillowMainURL);
        }
        
        // After all images are loaded, update to step 1
        setProcessingStep(1);
        
        // Automatically proceed to step 2 (assessment) handled by useEffect
      } catch (error) {
        console.error('Error processing images:', error);
        const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
        setError(errorMessage);
        setShowProcessing(false);
        
        // Track error
        trackError('image_processing', errorMessage, 'address_search');
      }
    } catch (error) {
      console.error('Error processing images:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      setError(errorMessage);
      setShowProcessing(false);
      
      // Track error
      trackError('address_save', errorMessage, 'address_search');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle address changes
  const handleAddressChange = (value: string) => {
    setLocalAddress(value);
    setDismissedSuggestion(false);
    // Clear structured data when user manually types
    if (structuredAddressData) {
      console.log('Clearing structured address data due to manual typing');
      setStructuredAddressData(null);
    }
  };

  // Handle upload link click
  const handleUploadLinkClick = () => {
    // Reset modal state when opening
    setShowWebcam(false);
    setError(null);
    setShowImageUploadModal(true);
  };

  // Scroll animation ref and values
  const stepsRef = React.useRef<HTMLDivElement>(null);
  const scrollTrackRef = React.useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: stepsRef,
    offset: ["start end", "end start"]
  });

  // Track window width for responsive calculations
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 0);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle upload message cycling
  useEffect(() => {
    if (isGeneratingFromUpload) {
      // Clear any existing interval
      if (uploadSubtextIntervalRef.current) {
        clearInterval(uploadSubtextIntervalRef.current);
      }

      // Start cycling through subtexts with same logic as step 3
      uploadSubtextIntervalRef.current = setInterval(() => {
        setUploadSubtextIndex(prevIndex => {
          const currentSubtexts = getCurrentAISubtexts();
          
          if (singleImageMode) {
            // For single image mode, cycle through all messages normally
            return (prevIndex + 1) % currentSubtexts.length;
          } else {
            // Original logic for multi-image mode: Messages: 0,1,2,3,4,5,6 then repeat 4,5,6
            if (prevIndex < 6) {
              return prevIndex + 1;
            } else {
              // After message 6, go back to message 4
              return 4;
            }
          }
        });
      }, 3000); // 3 second intervals
    } else {
      // Clear interval when not generating
      if (uploadSubtextIntervalRef.current) {
        clearInterval(uploadSubtextIntervalRef.current);
      }
      setUploadSubtextIndex(0);
    }

    // Cleanup function
    return () => {
      if (uploadSubtextIntervalRef.current) {
        clearInterval(uploadSubtextIntervalRef.current);
      }
    };
  }, [isGeneratingFromUpload, singleImageMode]);

  // Handle upload progress percentage counter (same as step 3)
  useEffect(() => {
    if (isGeneratingFromUpload) {
      // Reset percentage when starting
      setUploadProgressPercentage(0);
      
      // Step 3 duration: 100 seconds
      const duration = 100000; // 100 seconds in milliseconds
      const interval = 100; // Update every 100ms for smooth animation
      const increment = (100 / duration) * interval;

      const timer = setInterval(() => {
        setUploadProgressPercentage(prev => {
          const newPercentage = prev + increment;
          if (newPercentage >= 100) {
            clearInterval(timer);
            return 100;
          }
          return newPercentage;
        });
      }, interval);

      // Cleanup timer when generation stops
      return () => {
        clearInterval(timer);
      };
    } else {
      // Reset percentage when not generating
      setUploadProgressPercentage(0);
    }
  }, [isGeneratingFromUpload]);

  // Handle testimonial cycling
  useEffect(() => {
    // Clear any existing interval
    if (testimonialIntervalRef.current) {
      clearInterval(testimonialIntervalRef.current);
    }

    // Start cycling through testimonials every 3 seconds
    testimonialIntervalRef.current = setInterval(() => {
      setCurrentTestimonialIndex(prevIndex => (prevIndex + 1) % TESTIMONIALS.length);
    }, 3000);

    // Cleanup function
    return () => {
      if (testimonialIntervalRef.current) {
        clearInterval(testimonialIntervalRef.current);
      }
    };
  }, []); // Run once on mount

  // Calculate dynamic scroll amount based on screen width
  const getScrollXPixels = React.useMemo(() => {
    // Define our three control points
    const BREAKPOINTS = [
      { width: 360, scroll: -400 },
      { width: 480, scroll: -260 },
      { width: 740, scroll: 0 }
    ];
    
    // Below minimum width, use first breakpoint value
    if (windowWidth <= BREAKPOINTS[0].width) {
      return BREAKPOINTS[0].scroll;
    }
    
    // Above maximum width, use last breakpoint value
    if (windowWidth >= BREAKPOINTS[2].width) {
      return BREAKPOINTS[2].scroll;
    }
    
    // Find the two breakpoints to interpolate between
    for (let i = 0; i < BREAKPOINTS.length - 1; i++) {
      const currentBreakpoint = BREAKPOINTS[i];
      const nextBreakpoint = BREAKPOINTS[i + 1];
      
      if (windowWidth >= currentBreakpoint.width && windowWidth <= nextBreakpoint.width) {
        // Calculate interpolation factor
        const factor = (windowWidth - currentBreakpoint.width) / 
                      (nextBreakpoint.width - currentBreakpoint.width);
        
        // Linear interpolation between the two breakpoint values
        const scroll = currentBreakpoint.scroll + 
                      factor * (nextBreakpoint.scroll - currentBreakpoint.scroll);
        
        return Math.round(scroll);
      }
    }
    
    // Default fallback (should never reach here due to our range checks above)
    return 0;
  }, [windowWidth]);

  // Dynamic scroll transform with pixel-based movement
  const scrollX = useTransform(
    scrollYProgress,
    [0, 0.25, 0.55], // Delayed start and slower progression for better viewing
    ["0px", "0px", `${getScrollXPixels}px`]
  );

  useEffect(() => {
    const homeImageTimer = setInterval(() => {
      if (generatedImages.watercolor) return; // Don't rotate if we have an AI generated image
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % carouselImages.length);
    }, 3000);

    return () => clearInterval(homeImageTimer);
  }, [generatedImages.watercolor, carouselImages.length]);

  // Helper function to get dynamic title text based on address availability and mode
  const getTitleText = () => {
    if (selectedMode === 'pet') {
      // Pet mode text
      return 'Turn your pet into beautifully framed AI art';
    }
    
    // Home mode text
    // First check structuredAddressData (for manual autocomplete entries)
    if (structuredAddressData?.address_line_1) {
      return (
        <>
          Turn your <strong>{structuredAddressData.address_line_1}</strong> home into beautifully framed AI art
        </>
      );
    }
    
    // Then check FrameContext structured address fields (for /h route database data)
    if (addressLine1) {
      return (
        <>
          Turn your <strong>{addressLine1}</strong> home into beautifully framed AI art
        </>
      );
    }
    
    return 'Turn your home into beautifully framed AI art';
  };

  return (
    <div className="master-container">
      {/* Test Mode Badge - only show when in test mode */}
      {!isLiveMode && (
        <div className="absolute top-4 left-4 z-[200]">
          <div className="bg-green-600 text-white px-3 py-1 rounded-full text-sm font-medium shadow-lg">
            TEST MODE
          </div>
        </div>
      )}
      
      {/* Transition Toast Notification */}
      {isTransitioningToGenerated && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[300] bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-pulse">
          <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
          <span className="font-medium">✨ Your custom artwork is now ready!</span>
        </div>
      )}
      
      {/* USA Flag in top right corner */}
      <div 
        className="absolute top-4 right-4 z-[200]"
        onMouseEnter={() => setShowUSAPopup(true)}
        onMouseLeave={() => setShowUSAPopup(false)}
      >
        <button
          onClick={() => setShowUSAPopup(!showUSAPopup)}
          className="flex items-center gap-2 text-2xl hover:opacity-80 transition-opacity cursor-pointer bg-white/70 backdrop-blur-sm rounded-full px-4 py-2 shadow-sm border border-white/30 max-[479px]:flex-row-reverse"
        >
          <AmericanFlag />
          <span className="text-sm font-medium text-gray-700 max-[479px]:hidden">USA</span>
          <ChevronDown className={`w-4 h-4 text-gray-700 transition-transform ${showUSAPopup ? 'rotate-180' : ''}`} />
        </button>
        
        {/* USA Availability Popup */}
        {showUSAPopup && (
          <div 
            className="absolute top-full right-0 mt-2 w-[270px] bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-[200] cursor-pointer"
            onClick={() => setShowUSAPopup(false)}
          >
            <div>
              <p className="text-sm text-gray-700">
                Frame my home is currently only available in the USA.
              </p>
            </div>
          </div>
        )}
      </div>
      
      {/* Decorative elements - only visible on desktop */}
      <img
        src="https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/website_assets//plant-leafs.png"
        alt="Plant Leaves"
        className="plant-decoration plant-decoration--left"
      />
      <img
        src="https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/website_assets//plant-pot%20-blur.png"
        alt="Hanging Plant"
        className="plant-decoration plant-decoration--right"
      />

      {/* Promotional Banner */}
      <div className="w-full bg-black text-white py-2 px-4 text-center">
        <p className="text-xs font-medium">
          Limited offer! Order now and claim a 20% discount.
        </p>
      </div>

      {/* Main viewport-sized container */}
      <div className="main-viewport-container">
        {/* Top bar with white background */}
        <div className="top-bar">
          {/* Logo */}
          <div className="logo-container">
            <img 
              src="https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/website_assets//Framemyhome.png"
              alt="Framemyhome"
              className="logo-image"
            />
          </div>
        </div>

        {/* Home/Pet Toggle - moved back below logo */}
        <div className="flex justify-center bg-white pb-4">
          <div className="flex items-center bg-gray-100 rounded-full p-1">
            <button
              onClick={() => {
                // Track mode switch in widget
                if (selectedMode !== 'home') {
                  window.trackInteraction?.('mode_switched', {
                    from: selectedMode,
                    to: 'home',
                    at_stage: 'landing'
                  });
                }
                setSelectedMode('home');
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                selectedMode === 'home'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Home className="w-4 h-4" />
              for homes
            </button>
            <button
              onClick={() => {
                // Track mode switch in widget
                if (selectedMode !== 'pet') {
                  window.trackInteraction?.('mode_switched', {
                    from: selectedMode,
                    to: 'pet',
                    at_stage: 'landing'
                  });
                }
                setSelectedMode('pet');
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                selectedMode === 'pet'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <PawPrint className="w-4 h-4" />
              for pets
            </button>
          </div>
        </div>

        {/* Subtitle - moved below toggle */}
        <div className="text-center bg-white" style={{ paddingTop: '0px', paddingBottom: '16px' }}>
          <p className="text-gray-600 text-xs md:text-sm max-[479px]:max-w-80 max-[479px]:mx-auto font-bold">
            {getTitleText()}
          </p>
        </div>

        {/* Frame display container */}
        <div className="frame-display-container">
          <div className="frame-aspect">
            {/* Images display */}
            <div className="images-container">
              {generatedImages.watercolor ? (
                <img
                  src={generatedImages.watercolor}
                  alt="AI Generated Watercolor"
                  className="carousel-image carousel-image--active"
                />
              ) : (
                carouselImages.map((image, index) => (
                  <img
                    key={image}
                    src={image}
                    alt={`AI generated illustration ${index + 1}`}
                    className={`carousel-image ${
                      index === currentImageIndex ? 'carousel-image--active' : ''
                    }`}
                  />
                ))
              )}
            </div>
            <img
              src="https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//Black_M.png"
              alt="Wooden Frame"
              className="frame-overlay"
            />
          </div>
        </div>

        {/* Dots container */}
        <div className="dots-container">
          {!generatedImages.watercolor && (
            <div className="pagination">
              {carouselImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`pagination-dot ${
                    index === currentImageIndex ? 'pagination-dot--active' : ''
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Input container */}
        <div className="input-container">
          <div className="input-with-link-container">
            {hasReturnedFromConfigurator ? (
              // Two buttons for returning users
              <div className="search-container">
                {error && (
                  <div className={error.includes("couldn't find a suitable image") ? "text-gray-900 text-sm mb-3 text-center" : `message ${getMessageType(error)}`}>
                    {error}
                  </div>
                )}
                <div className="flex flex-col min-[480px]:flex-row gap-2 w-full items-center">
                  <button
                    onClick={handleTryNewAddress}
                    className="flex-1 w-full py-4 px-[1.875rem] md:px-6 bg-white border border-gray-300 text-gray-700 font-medium rounded-full hover:bg-gray-50 transition-colors text-base md:text-lg"
                  >
                    {selectedMode === 'pet' ? 'Upload Another Image' : 'Try New Address'}
                  </button>
                  <button
                    onClick={handleConfigureAndBuy}
                    className="flex-1 w-full py-4 px-[1.875rem] md:px-6 bg-black text-white font-medium rounded-full hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 text-base md:text-lg"
                  >
                    {getConfigureButtonText()}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : selectedMode === 'pet' ? (
              // Pet mode - show upload button instead of address input
              <div className="search-container">
                {error && (
                  <div className={error.includes("couldn't find a suitable image") ? "text-gray-900 text-sm mb-3 text-center" : `message ${getMessageType(error)}`}>
                    {error}
                  </div>
                )}
                <div className="relative flex items-center justify-center">
                  <img 
                    src="/FMH_Arrow.png" 
                    alt="Arrow" 
                    className="absolute left-[-35px] w-8 h-8 hidden md:block"
                  />
                  <div className="relative flex-1 max-w-md mx-auto">
                    {/* Pulsing animation background */}
                    <div className="absolute inset-0 bg-black rounded-full animate-pulse opacity-20"></div>
                    <button
                      onClick={handleUploadLinkClick}
                      className="relative w-full py-4 px-[1.875rem] md:px-6 bg-black text-white font-medium rounded-full hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 text-base md:text-lg"
                    >
                      <PawPrint className="w-5 h-5" />
                      Upload a pet image
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ) : showAddressInput ? (
              // Home mode - address input form (shown after clicking "Input an address")
              <div className="search-container">
                <div className="relative flex items-center justify-center">
                  <img 
                    src="/FMH_Arrow.png" 
                    alt="Arrow" 
                    className="absolute left-[-35px] w-8 h-8 hidden md:block"
                  />
                  <form onSubmit={handleSubmit} className="search-form flex-1 max-w-md mx-auto">
                    {error && (
                      <div className={error.includes("couldn't find a suitable image") ? "text-gray-900 text-sm mb-3 text-center" : `message ${getMessageType(error)}`}>
                        {error}
                      </div>
                    )}
                    {!useGoogleMaps && showSuggestion && suggestedAddress && (
                      <div className="address-suggestion flex justify-between items-center">
                        <span 
                          className="flex-1 cursor-pointer"
                          onClick={() => {
                            setLocalAddress(suggestedAddress);
                            setShowSuggestion(false);
                          }}
                        >
                          {suggestedAddress}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowSuggestion(false);
                            setDismissedSuggestion(true);
                          }}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    
                    {useGoogleMaps ? (
                      <div className="pl-2">
                        <PlacesAutocomplete
                          value={localAddress}
                          onChange={handleAddressChange}
                          onSelect={handleAddressSelect}
                          placeholder="Add an address to start"
                          disabled={isSubmitting}
                          error={!!error}
                          onFocus={() => setIsInputFocused(true)}
                          onBlur={() => setIsInputFocused(false)}
                        />
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={localAddress}
                        onChange={(e) => setLocalAddress(e.target.value)}
                        placeholder="Add an address to start"
                        className={`search-input ${error ? 'search-input--error' : ''}`}
                        disabled={isSubmitting}
                        onFocus={() => setIsInputFocused(true)}
                        onBlur={() => setIsInputFocused(false)}
                      />
                    )}
                    <button
                      disabled={isSubmitting}
                      type="submit"
                      className="search-button"
                    >
                      {isSubmitting ? (
                        <Loader2 className="spinner" />
                      ) : (
                        <ArrowRight />
                      )}
                    </button>
                  </form>
                </div>
              </div>
            ) : (
              // Home mode - default two buttons for new users
              <div className="search-container">
                {error && (
                  <div className={error.includes("couldn't find a suitable image") ? "text-gray-900 text-sm mb-3 text-center" : `message ${getMessageType(error)}`}>
                    {error}
                  </div>
                )}
                <div className="flex flex-col min-[480px]:flex-row gap-2 w-full items-center">
                  <button
                    onClick={handleInputAddressClick}
                    className="flex-1 w-full py-4 px-[1.875rem] md:px-6 bg-white border border-gray-300 text-gray-700 font-medium rounded-full hover:bg-gray-50 transition-colors text-base md:text-lg"
                  >
                    Use an address
                  </button>
                  <button
                    onClick={handleUploadPictureClick}
                    className="flex-1 w-full py-4 px-[1.875rem] md:px-6 bg-black text-white font-medium rounded-full hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 text-base md:text-lg"
                  >
                    Upload a picture
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
            
            {/* Upload link with 40px bottom padding (20px original + 20px added) */}
            {!hasReturnedFromConfigurator && selectedMode === 'home' && showAddressInput && (
              <div className="pb-10 max-[479px]:pb-0">
                <button
                  onClick={handleUploadLinkClick}
                  className="upload-link"
                >
                  or upload an image
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Steps Section */}
      <div className="steps-section" ref={stepsRef}>
        <div className="steps-container">
          <h2 className="steps-heading">
            {selectedMode === 'pet' ? 'Frame your pet in 3 simple steps.' : 'Frame your home in 3 simple steps.'}
          </h2>
          
          {windowWidth < 768 ? (
            <div className="auto-scroll-container">
              <motion.div 
                ref={scrollTrackRef}
                className="scroll-track"
                style={{ x: scrollX, paddingLeft: '1rem' }}
              >
                <div className="steps-grid mobile-steps-grid">
                  <div className="step-item">
                    <div className="step-image-container">
                      <img 
                        src="/Step1.png"
                        alt="Step 1"
                        className="w-full h-full object-cover rounded-xl"
                      />
                      {/* Watercolor overlay */}
                      <img 
                        src={getBestImageForStyle('watercolor')}
                        alt="Watercolor example"
                        className="watercolor-overlay absolute top-[calc(50%+39px)] left-[calc(50%+24px)] transform -translate-x-1/2 -translate-y-1/2 w-[22%] h-auto object-contain shadow-sm"
                      />
                    </div>
                  </div>
                  <div className="step-item">
                    <div className="step-image-container">
                      <img 
                        src="/Step2.png"
                        alt="Step 2"
                        className="w-full h-full object-cover rounded-xl"
                      />
                      {/* Pencil overlay for step 2 - different style */}
                      <img 
                        src={getBestImageForStyle('pencil')}
                        alt="Pencil example"
                        className="watercolor-overlay absolute top-[calc(50%+23px)] left-[calc(50%-22px)] transform -translate-x-1/2 -translate-y-1/2 w-[47%] h-auto object-contain shadow-sm"
                      />
                    </div>
                  </div>
                  <div className="step-item">
                    <div className="step-image-container">
                      <img 
                        src="/Step3.png"
                        alt="Step 3"
                        className="w-full h-full object-cover rounded-xl"
                      />
                      {/* Address box for step 3 */}
                      <div className="absolute top-[calc(50%+64px)] left-[calc(50%+31px)] transform -translate-x-1/2 -translate-y-1/2 w-[27%] bg-white p-2 text-left">
                        <div className="text-[5px] font-medium text-gray-900 leading-tight">
                          {address ? (
                            <>
                              <div>The Owner</div>
                              <div>&nbsp;</div>
                              <div>742 Evergreen Terrace</div>
                              <div>Springfield</div>
                            </>
                          ) : (
                            <>
                              <div>The Owner</div>
                              <div>&nbsp;</div>
                              <div>742 Evergreen Terrace</div>
                              <div>Springfield</div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          ) : (
            <div className="steps-grid">
              <div className="step-item">
                <div className="step-image-container">
                  <img 
                    src="/Step1.png"
                    alt="Step 1"
                    className="w-full h-full object-cover rounded-xl"
                  />
                  {/* Watercolor overlay */}
                  <img 
                    src={getBestImageForStyle('watercolor')}
                    alt="Watercolor example"
                    className="watercolor-overlay absolute top-[calc(50%+50px)] left-[calc(50%+32px)] transform -translate-x-1/2 -translate-y-1/2 w-[22%] h-auto object-contain shadow-sm"
                  />
                </div>
              </div>
              <div className="step-item">
                <div className="step-image-container">
                  <img 
                    src="/Step2.png"
                    alt="Step 2"
                    className="w-full h-full object-cover rounded-xl"
                  />
                  {/* Pencil overlay for step 2 - different style */}
                  <img 
                    src={getBestImageForStyle('pencil')}
                    alt="Pencil example"
                    className="watercolor-overlay absolute top-[calc(50%+30px)] left-[calc(50%-29px)] transform -translate-x-1/2 -translate-y-1/2 w-[47%] h-auto object-contain shadow-sm"
                  />
                </div>
              </div>
              <div className="step-item">
                <div className="step-image-container">
                  <img 
                    src="/Step3.png"
                    alt="Step 3"
                    className="w-full h-full object-cover rounded-xl"
                  />
                  {/* Address box for step 3 */}
                  <div className="absolute top-[calc(50%+78px)] left-[calc(50%+38px)] transform -translate-x-1/2 -translate-y-1/2 w-[27%] bg-white p-3 text-left">
                    <div className="text-[5px] font-medium text-gray-900 leading-tight">
                      {address ? (
                        <>
                          <div>The Owner</div>
                          <div>&nbsp;</div>
                          <div>742 Evergreen Terrace</div>
                          <div>Springfield</div>
                        </>
                      ) : (
                        <>
                          <div>The Owner</div>
                          <div>&nbsp;</div>
                          <div>742 Evergreen Terrace</div>
                          <div>Springfield</div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Customer Testimonials Section */}
      <div className="bg-gray-50 py-16 max-[479px]:min-h-[330px]">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center">
            {/* Testimonial dots indicator */}
            <div className="flex justify-center gap-2 mb-8">
              {TESTIMONIALS.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonialIndex(index)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentTestimonialIndex ? 'bg-gray-700' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
            
            <div className="min-h-[120px] flex items-center justify-center">
              <div className="max-w-[800px] mx-auto">
                <blockquote className="text-lg md:text-xl text-gray-700 italic mb-6 leading-relaxed">
                  "{TESTIMONIALS[currentTestimonialIndex].quote}"
                </blockquote>
                <div className="text-gray-900 font-medium">
                  {TESTIMONIALS[currentTestimonialIndex].author}
                </div>
                <div className="text-gray-600 text-sm">
                  {TESTIMONIALS[currentTestimonialIndex].location}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Styles Section */}
      <div className="styles-section">
        <div className="styles-container">
          <h2 className="styles-heading">
            {selectedMode === 'pet' ? 'Your favorite companion, captured in your favorite style' : 'Your favorite place, captured in your favorite style'}
          </h2>
          <p className="styles-subheading">
            {selectedMode === 'pet' ? 'Preview your pet instantly with AI in 3 popular illustration styles' : 'Preview your home instantly with AI in 3 popular illustration styles'}
          </p>
          
          <div className="styles-grid">
            <div className="style-item">
              <img 
                src={getBestImageForStyle('watercolor')}
                alt="Watercolor Style"
                className="style-image"
                style={{ border: '5px solid white' }}
              />
              <h3 className="style-title">Watercolor</h3>
              <p className="style-subtitle">Cold-pressed fine art paper</p>
              <p className="style-description">
                {selectedMode === 'pet' ? 
                  'Soft, dreamy aesthetics with vibrant flowing colors that capture the essence of your pet with a gentle, artistic touch.' :
                  'Soft, dreamy aesthetics with vibrant flowing colors that capture the essence of your home with a gentle, artistic touch.'
                }
              </p>
            </div>
            
            <div className="style-item">
              <img 
                src={getBestImageForStyle('pencil')}
                alt="Pencil Style"
                className="style-image"
                style={{ border: '5px solid white' }}
              />
              <h3 className="style-title">Pencil</h3>
              <p className="style-subtitle">Hot-pressed fine art paper</p>
              <p className="style-description">
                {selectedMode === 'pet' ? 
                  'Detailed line work with subtle shading that brings out the character and fine details of your beloved companion.' :
                  'Detailed line work with subtle shading that brings out the architectural character and fine details of your home.'
                }
              </p>
            </div>
            
            <div className="style-item">
              <img 
                src={getBestImageForStyle('oil')}
                alt="Oil Style"
                className="style-image"
                style={{ border: '5px solid white' }}
              />
              <h3 className="style-title">Oil</h3>
              <p className="style-subtitle">Somerset velvet fine art paper</p>
              <p className="style-description">
                {selectedMode === 'pet' ? 
                  'Textured, matte finish with a timeless feel that creates a nostalgic rendition of your pet with soft, diffused edges.' :
                  'Textured, matte finish with a timeless feel that creates a nostalgic rendition of your home with soft, diffused edges.'
                }
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Container Section */}
      <div className="container-section">
        <div className="container-content">
          <div className="container-text">
            <h2 className="container-title">
              {selectedMode === 'pet' ? 
                'Celebrate your pet with a beautiful frame' :
                'Celebrate your home with a beautiful frame'
              }
            </h2>
            <p className="container-subtitle">
              Our frames come in 5 different styles and 2 different sizes.
            </p>
          </div>
          
          <div className="container-frames" style={{
            backgroundImage: `url(${getBestContainerImage()})`,
            backgroundSize: '50% auto',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}>
            {[
              'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//Gold_M.png',
              'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//Mocca_M.png',
              'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//Natural_M.png',
              'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//White_M.png',
              'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//Black_M.png'
            ].map((frame, index) => (
              <img
                key={frame}
                src={frame}
                alt={`Frame style ${index + 1}`}
                className={`frame-image ${index === currentImageIndex % 5 ? 'active' : ''}`}
              />
            ))}
            <div className="frame-dots">
              {[0, 1, 2, 3, 4].map((index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`frame-dot ${index === currentImageIndex % 5 ? 'active' : ''}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* White Section */}
      <div className="white-section">
        <img
          src="https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/website_assets//Framemyhome.png"
          alt="Framemyhome"
          className="white-section-logo"
        />
        <div className="white-section-input">
          <div className="input-with-link-container">
            {hasReturnedFromConfigurator ? (
              // Two buttons for returning users
              <div className="search-container">
                {error && (
                  <div className={error.includes("couldn't find a suitable image") ? "text-gray-900 text-sm mb-3 text-center" : `message ${getMessageType(error)}`}>
                    {error}
                  </div>
                )}
                <div className="flex flex-col min-[480px]:flex-row gap-2 w-full items-center">
                  <button
                    onClick={handleTryNewAddress}
                    className="flex-1 w-full py-4 px-[1.875rem] md:px-6 bg-white border border-gray-300 text-gray-700 font-medium rounded-full hover:bg-gray-50 transition-colors text-base md:text-lg"
                  >
                    {selectedMode === 'pet' ? 'Upload Another Image' : 'Try New Address'}
                  </button>
                  <button
                    onClick={handleConfigureAndBuy}
                    className="flex-1 w-full py-4 px-[1.875rem] md:px-6 bg-black text-white font-medium rounded-full hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 text-base md:text-lg"
                  >
                    {getConfigureButtonText()}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : selectedMode === 'pet' ? (
              // Pet mode - show upload button instead of address input
              <div className="search-container">
                {error && (
                  <div className={error.includes("couldn't find a suitable image") ? "text-gray-900 text-sm mb-3 text-center" : `message ${getMessageType(error)}`}>
                    {error}
                  </div>
                )}
                <div className="relative flex items-center justify-center">
                  <img 
                    src="/FMH_Arrow.png" 
                    alt="Arrow" 
                    className="absolute left-[-35px] w-8 h-8 hidden md:block"
                  />
                  <div className="relative flex-1 max-w-md mx-auto">
                    {/* Pulsing animation background */}
                    <div className="absolute inset-0 bg-black rounded-full animate-pulse opacity-20"></div>
                    <button
                      onClick={handleUploadLinkClick}
                      className="relative w-full py-4 px-[1.875rem] md:px-6 bg-black text-white font-medium rounded-full hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 text-base md:text-lg"
                    >
                      <PawPrint className="w-5 h-5" />
                      Upload a pet image
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ) : showAddressInput ? (
              // Home mode - address input form (shown after clicking "Input an address")
              <div className="search-container">
                <div className="relative flex items-center justify-center">
                  <img 
                    src="/FMH_Arrow.png" 
                    alt="Arrow" 
                    className="absolute left-[-35px] w-8 h-8 hidden md:block"
                  />
                  <form onSubmit={handleSubmit} className="search-form flex-1 max-w-md mx-auto">
                    {error && (
                      <div className={error.includes("couldn't find a suitable image") ? "text-gray-900 text-sm mb-3 text-center" : `message ${getMessageType(error)}`}>
                        {error}
                      </div>
                    )}
                    {!useGoogleMaps && showSuggestion && suggestedAddress && (
                      <div className="address-suggestion flex justify-between items-center">
                        <span 
                          className="flex-1 cursor-pointer"
                          onClick={() => {
                            setLocalAddress(suggestedAddress);
                            setShowSuggestion(false);
                          }}
                        >
                          {suggestedAddress}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowSuggestion(false);
                            setDismissedSuggestion(true);
                          }}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    
                    {useGoogleMaps ? (
                      <div className="pl-2">
                        <PlacesAutocomplete
                          value={localAddress}
                          onChange={handleAddressChange}
                          onSelect={handleAddressSelect}
                          placeholder="Add an address to start"
                          disabled={isSubmitting}
                          error={!!error}
                          onFocus={() => setIsInputFocused(true)}
                          onBlur={() => setIsInputFocused(false)}
                        />
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={localAddress}
                        onChange={(e) => setLocalAddress(e.target.value)}
                        placeholder="Add an address to start"
                        className={`search-input ${error ? 'search-input--error' : ''}`}
                        disabled={isSubmitting}
                        onFocus={() => setIsInputFocused(true)}
                        onBlur={() => setIsInputFocused(false)}
                      />
                    )}
                    <button
                      disabled={isSubmitting}
                      type="submit"
                      className="search-button"
                    >
                      {isSubmitting ? (
                        <Loader2 className="spinner" />
                      ) : (
                        <ArrowRight />
                      )}
                    </button>
                  </form>
                </div>
              </div>
            ) : (
              // Home mode - default two buttons for new users
              <div className="search-container">
                {error && (
                  <div className={error.includes("couldn't find a suitable image") ? "text-gray-900 text-sm mb-3 text-center" : `message ${getMessageType(error)}`}>
                    {error}
                  </div>
                )}
                <div className="flex flex-col min-[480px]:flex-row gap-2 w-full items-center">
                  <button
                    onClick={handleInputAddressClick}
                    className="flex-1 w-full py-4 px-[1.875rem] md:px-6 bg-white border border-gray-300 text-gray-700 font-medium rounded-full hover:bg-gray-50 transition-colors text-base md:text-lg"
                  >
                    Use an address
                  </button>
                  <button
                    onClick={handleUploadPictureClick}
                    className="flex-1 w-full py-4 px-[1.875rem] md:px-6 bg-black text-white font-medium rounded-full hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 text-base md:text-lg"
                  >
                    Upload a picture
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
            
            {/* Upload link - only show for home mode and new users */}
            {!hasReturnedFromConfigurator && selectedMode === 'home' && (
              <button
                onClick={handleUploadLinkClick}
                className="upload-link"
              >
                or upload an image
              </button>
            )}
          </div>
        </div>
        <img
          src="https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/website_assets//Sofa.png"
          alt="Sofa"
          className="white-section-image"
        />
      </div>

      {/* Minimal Footer */}
      <div className="bg-[#F5ECE1] py-8">
        <div className="flex justify-center items-center gap-6 text-xs text-gray-500">
          <Link to="/privacy" className="hover:text-gray-700 transition-colors">
            Privacy
          </Link>
          <span>•</span>
          <Link to="/terms" className="hover:text-gray-700 transition-colors">
            Terms & Conditions
          </Link>
          <span>•</span>
          <a 
            href="mailto:support@framemyhome.ai" 
            className="hover:text-gray-700 transition-colors"
          >
            Support
          </a>
        </div>
      </div>

      {/* Processing Popup */}
      <ProcessingPopup 
        isOpen={showProcessing} 
        currentStep={processingStep}
        streetViewImage={receivedImage}
        zillowImages={zillowImages}
        webImages={webImages}
        onGenerateAI={handleGenerateAI}
        isGeneratingAI={isGeneratingAI}
        onAssessSuitability={handleImageAssessment}
        isAssessing={isAssessing}
        selectedImage={selectedImage}
        selectionReason={selectionReason}
        onCreateSheet={handleCreateContactSheet}
        isCreatingSheet={isCreatingSheet}
        showImages={showImages}
        onToggleShowImages={() => setShowImages(!showImages)}
        onUploadImage={handleUploadImage}
        allImagesCount={allImagesCount}
        singleImageMode={singleImageMode}
        onToggleSingleImageMode={setSingleImageMode}
        onClose={() => setShowProcessing(false)}
      />
    
      {/* QR Code Modal */}
      {showQRCode && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[1000]">
          <div className="bg-white p-8 rounded-lg max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold">Property QR Code</h3>
              <button
                onClick={() => setShowQRCode(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex justify-center mb-6">
              <QRCodeSVG
                value={`https://courageous-gingersnap-4f44cf.netlify.app/?code=${isChapelHouse ? 'chapel' : ''}`}
                size={256}
                level="H"
                includeMargin
              />
            </div>
            <p className="text-sm text-gray-600 text-center break-all">
              <span className="block font-medium mb-2">Share this link:</span>
              https://courageous-gingersnap-4f44cf.netlify.app/?code={isChapelHouse ? 'chapel' : ''}
            </p>
          </div>
        </div>
      )}

      {/* Image Upload Modal */}
      {showImageUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[1000]">
          <div className="bg-[#F5ECE1] rounded-2xl max-w-md w-full max-h-[90vh] min-h-[460px] overflow-auto relative">
            {/* Close button overlaid on content */}
              <button
                onClick={() => {
                  setShowImageUploadModal(false);
                setShowEmailCollection(false);
                setShowWebcam(false);
                
                // Only clean up if email was provided (allowing complete flow reset)
                if (hasProvidedEmailForUpload) {
                  // Clean up preview URL when closing modal
                  if (uploadedImagePreview) {
                    URL.revokeObjectURL(uploadedImagePreview);
                    setUploadedImagePreview(null);
                  }
                  setEmailCollectionEmail('');
                  setEmailCollectionError('');
                  setGeneratedWatercolorUrl(null);
                  setIsFromUploadFlow(false);
                  setHasProvidedEmailForUpload(false);
                } else {
                  // Keep the state for re-prompting if they click Configure Frame
                  // Just clear the email input error
                  setEmailCollectionError('');
                }
                }}
              className="absolute top-4 right-4 z-10 text-gray-500 hover:text-gray-700 rounded-full p-1 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            
            {/* Conditional padding - minimal for email collection, standard for other states */}
            <div className={showEmailCollection ? "px-3 pt-3 pb-3 min-[480px]:pb-6" : "p-6"}>
              {showEmailCollection ? (
                <div className="text-center">
                  {/* Email Collection Screen */}
                  <div>
                  {/* Title Above Frame */}
                  <h3 className="text-lg font-semibold text-gray-900 px-3 mb-2 pt-5">
                    Your custom artwork<br />is ready
                  </h3>
                    
                    {/* Frame Image with Artwork - Full Width with Max Height */}
                    <div className="flex justify-center">
                      <div className="w-full relative max-h-[40vh] aspect-[16/14]">
                        {/* Watercolor image behind frame */}
                        {generatedWatercolorUrl && (
                          <div className="absolute inset-[21.6%] overflow-hidden rounded-sm">
                            <img
                              src={generatedWatercolorUrl}
                              alt="Generated artwork"
                              className="absolute inset-0 w-full h-full object-cover"
                            />
                          </div>
                        )}
                        {/* Black Frame Overlay */}
                        <img
                          src="https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates/Black_M.png"
                          alt="Black frame"
                          className="relative w-full h-full object-contain"
                        />
                      </div>
            </div>
            
                  <p className="text-sm text-gray-600 mb-3 mt-3 px-3 max-w-[370px] mx-auto">
                    Please provide your email to explore personalized styles and frames
                  </p>
                    
                    {/* Email Input with Arrow Button Inside */}
                    <div className="relative px-3 mb-4">
                      <input
                        type="email"
                        value={emailCollectionEmail}
                        onChange={(e) => {
                          setEmailCollectionEmail(e.target.value);
                          setEmailCollectionError('');
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleEmailCollectionSubmit();
                          }
                        }}
                        placeholder="Enter your email"
                        className={`w-full px-4 py-3 pr-14 border rounded-full focus:outline-none focus:ring-2 focus:ring-black text-sm min-[480px]:text-base ${
                          emailCollectionError ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                    <button
                      onClick={handleEmailCollectionSubmit}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors"
                      aria-label="Submit email"
                    >
                        <svg
                          className="w-5 h-5 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </button>
                    </div>
                  
                  {emailCollectionError && (
                    <p className="text-red-500 text-sm mt-2 text-left px-3">{emailCollectionError}</p>
                  )}
                  </div>
                </div>
              ) : showWebcam && !isGeneratingFromUpload ? (
                <div className="text-center">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6">Upload Picture</h3>
                  <WebcamCapture 
                    ref={webcamRef}
                    onCapture={handleImageCapture} 
                    onBack={() => setShowWebcam(false)} 
                  />
                </div>
              ) : (
                <>
                  {isGeneratingFromUpload ? (
                    <div className="text-center">
                      <h3 className="text-xl font-semibold text-gray-900 mb-6">Upload Picture</h3>
                      {/* Show uploaded image preview with overlay and percentage counter at the top */}
                      {uploadedImagePreview && (
                        <div className="mb-6 flex justify-center">
                          <div className="relative w-[40%] aspect-square">
                            <img 
                              src={uploadedImagePreview} 
                              alt="Uploaded preview" 
                              className="w-full h-full object-cover rounded-lg shadow-sm"
                            />
                            <div className="absolute inset-0 bg-white bg-opacity-50 rounded-lg"></div>
                            {/* Progress spinner positioned on top of image */}
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="relative">
                                <div className="w-16 h-16 border-4 border-gray-200 border-t-black rounded-full animate-spin"></div>
                                {/* Percentage counter in center of spinner */}
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <div className="bg-black bg-opacity-75 text-white px-2 py-1 rounded text-xs font-normal" style={{ outline: '1px solid rgba(0, 0, 0, 0.3)' }}>
                                    {Math.round(uploadProgressPercentage)}%
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* If no image preview, show spinner separately with percentage counter */}
                      {!uploadedImagePreview && (
                      <div className="mb-6 relative flex justify-center">
                          <div className="relative">
                            <div className="w-16 h-16 border-4 border-gray-200 border-t-black rounded-full animate-spin"></div>
                            {/* Percentage counter in center of spinner */}
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="bg-black bg-opacity-75 text-white px-2 py-1 rounded text-xs font-normal" style={{ outline: '1px solid rgba(0, 0, 0, 0.3)' }}>
                                {Math.round(uploadProgressPercentage)}%
                              </div>
                            </div>
                          </div>
                      </div>
                      )}
                      
                      <h4 className="text-base font-medium text-gray-900 mb-2">Generating Images</h4>
                      <p className="text-gray-700">{getCurrentAISubtexts()[uploadSubtextIndex]}</p>
                      <p className="text-gray-500 text-sm mt-2">This may take up to a minute</p>
                      <p className="text-gray-400 text-xs mt-4">You will be redirected to the configurator when complete</p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <h3 className="text-xl font-semibold text-gray-900 mb-6">Upload Picture</h3>
                      
                      {error && (
                        <div className={`p-3 rounded-lg mb-4 ${
                          getMessageType(error) === 'message--server-busy' 
                            ? 'bg-yellow-50 text-yellow-800' 
                            : 'bg-red-50 text-red-600'
                        }`}>
                          {error}
                        </div>
                      )}
                      
                      <ImageDropzone 
                        onImageDrop={handleUploadImage}
                        className="mb-6" 
                      />
                      
                      <div className="flex justify-center">
                        <button
                          onClick={() => {
                            setShowWebcam(true);
                          }}
                          className="flex items-center gap-2 bg-gray-800 text-white px-4 py-2 rounded-full hover:bg-gray-700 transition-colors"
                        >
                          <Camera className="w-4 h-4" />
                          <span>Take a Photo</span>
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Prompt Editor Modal */}
      <PromptEditor 
        isOpen={showPromptEditor} 
        onClose={() => setShowPromptEditor(false)} 
      />

      {/* Decorative elements - only visible on desktop */}
    </div>
  );
};

export default CssLanding;